import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { overlayVariants, sheetVariants, transition } from '../lib/motion.js';
import { useEventStore } from '../store/useEventStore.js';
import { fetchAccountOverview, fetchBarUserById } from '../lib/account.js';
import { ALL_BADGE_CODES, getBadgeInfo } from '../lib/gamification.js';
import { toast } from '../store/useToast.js';

const AVATAR_PRESETS = ['🎧', '🦊', '🐺', '🦅', '🦉', '🐆', '🐍', '🐯', '🦁', '🎉', '🔥', '✨', '💃', '🕺', '👑', '🌟', '💎', '🎸'];

function isEmojiAvatar(v) {
  return Boolean(v) && !/^https?:\/\//i.test(v);
}

function Avatar({ value, size = 'h-16 w-16 text-3xl', onClick }) {
  const inner = isEmojiAvatar(value) ? (
    value
  ) : value ? (
    <img src={value} alt="" className="h-full w-full rounded-full object-cover" />
  ) : (
    '🎧'
  );
  const cls = `flex ${size} shrink-0 items-center justify-center rounded-full border border-border bg-bg-soft`;
  return onClick ? (
    <button type="button" onClick={onClick} className={cls} title="Changer l’avatar">
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-border bg-bg-soft px-2 py-3">
      <span className="text-xl font-bold tabular-nums text-white">{value}</span>
      <span className="mt-0.5 text-center text-[10px] uppercase tracking-wide text-white/45">{label}</span>
    </div>
  );
}

/**
 * Profil participant en pop-up. `profileUserId` null = mon profil (éditable) ;
 * sinon profil public d'un autre user (lecture seule : badges, stats).
 */
export default function ProfileSheet({ open, onClose, profileUserId = null, onManageAccount }) {
  const barUser = useEventStore((s) => s.barUser);
  const authEmail = useEventStore((s) => s.authEmail);
  const updateBarUsername = useEventStore((s) => s.updateBarUsername);
  const patchBarUser = useEventStore((s) => s.patchBarUser);

  const self = !profileUserId || profileUserId === barUser?.id;
  const targetId = self ? barUser?.id : profileUserId;

  const [overview, setOverview] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [pseudoBusy, setPseudoBusy] = useState(false);
  const [pseudoErr, setPseudoErr] = useState(null);
  const inputRef = useRef(null);

  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return;
    setOverview(null);
    setOtherProfile(null);
    setAvatarOpen(false);
    setEditing(false);
    setPseudoErr(null);
    let alive = true;
    (async () => {
      try {
        if (targetId) {
          const ov = await fetchAccountOverview(targetId);
          if (alive) setOverview(ov);
        }
        if (!self && profileUserId) {
          const p = await fetchBarUserById(profileUserId);
          if (alive) setOtherProfile(p);
        }
      } catch {
        if (alive) toast.error('Impossible de charger le profil pour le moment.');
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, targetId, self, profileUserId]);

  // Escape ferme la feuille (sauf pendant l'édition du pseudo, gérée à part).
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape' && !editing) onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, editing, onClose]);

  useEffect(() => {
    if (editing) {
      setDraft(barUser?.username ?? '');
      const id = window.setTimeout(() => inputRef.current?.focus(), 40);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [editing, barUser?.username]);

  const profile = self ? barUser : otherProfile;
  const username = profile?.username ?? '…';
  const avatar = profile?.avatar_url;
  const featured = profile?.featured_badge_code ?? null;

  const earnedByCode = useMemo(
    () => Object.fromEntries((overview?.badges ?? []).map((b) => [b.code, b])),
    [overview]
  );
  const totals = overview?.totals;

  async function savePseudo() {
    const trimmed = draft.trim();
    if (trimmed.length < 1) {
      setPseudoErr('Choisis au moins un caractère.');
      return;
    }
    setPseudoBusy(true);
    setPseudoErr(null);
    const res = await updateBarUsername(trimmed);
    setPseudoBusy(false);
    if (!res.ok) {
      setPseudoErr(res.message ?? 'Erreur.');
      return;
    }
    setEditing(false);
  }

  async function pickAvatar(emoji) {
    setAvatarOpen(false);
    setBusy(true);
    const res = await patchBarUser({ avatar_url: emoji });
    setBusy(false);
    if (!res?.ok) toast.error('Impossible de changer l’avatar. Réessaie.');
  }

  async function toggleFeatured(code) {
    if (!self || !earnedByCode[code]) return;
    setBusy(true);
    const res = await patchBarUser({ featured_badge_code: featured === code ? null : code });
    setBusy(false);
    if (!res?.ok) toast.error('Impossible de mettre à jour le badge. Réessaie.');
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-x-0 top-0 z-[85] flex h-[100dvh] items-end justify-center bg-black/75 backdrop-blur-md p-0 pt-[env(safe-area-inset-top)] sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Profil"
          variants={overlayVariants}
          initial={reduceMotion ? false : 'initial'}
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          transition={transition(reduceMotion, 'fast')}
          onClick={() => onClose?.()}
        >
          <motion.div
            className="sheet-surface max-h-[min(92dvh,760px)] w-full max-w-[480px] overflow-y-auto rounded-t-3xl border border-border p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-2xl sm:pb-5"
            variants={sheetVariants}
            initial={reduceMotion ? false : 'initial'}
            animate="animate"
            exit={reduceMotion ? undefined : 'exit'}
            transition={transition(reduceMotion, 'panel')}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête profil */}
            <div className="flex items-center gap-3">
              <Avatar value={avatar} onClick={self ? () => setAvatarOpen((v) => !v) : undefined} />
              <div className="min-w-0 flex-1">
                {self && editing ? (
                  <div className="flex flex-col gap-2">
                    <input
                      ref={inputRef}
                      className="input"
                      value={draft}
                      maxLength={32}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') savePseudo();
                        if (e.key === 'Escape') setEditing(false);
                      }}
                      placeholder="Ton pseudo"
                    />
                    <div className="flex items-center gap-2">
                      <button type="button" className="btn-primary px-3 py-1.5 text-xs" onClick={savePseudo} disabled={pseudoBusy}>
                        {pseudoBusy ? '…' : 'Enregistrer'}
                      </button>
                      <button type="button" className="btn-ghost px-3 py-1.5 text-xs" onClick={() => setEditing(false)}>
                        Annuler
                      </button>
                      <span className="ml-auto text-[10px] text-white/35">{draft.trim().length}/32</span>
                    </div>
                    {pseudoErr ? <p className="text-[11px] text-red-300">{pseudoErr}</p> : null}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <p className="truncate text-lg font-bold text-white">{username}</p>
                      {self ? (
                        <button
                          type="button"
                          onClick={() => setEditing(true)}
                          className="btn-ghost shrink-0 rounded-lg px-1.5 py-1 text-xs text-white/55"
                          aria-label="Modifier le pseudo"
                          title="Modifier le pseudo"
                        >
                          ✏️
                        </button>
                      ) : null}
                    </div>
                    {self ? (
                      authEmail ? (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-emerald-300/90">
                          <span aria-hidden>✓</span> Compte connecté
                        </p>
                      ) : (
                        <p className="mt-0.5 text-xs text-white/45">Mode invité</p>
                      )
                    ) : (
                      <p className="mt-0.5 text-xs text-white/45">Profil participant</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {self && avatarOpen ? (
              <div className="mt-3 flex flex-wrap gap-1.5 rounded-xl border border-border bg-black/25 p-2">
                {AVATAR_PRESETS.map((emo) => (
                  <button
                    key={emo}
                    type="button"
                    onClick={() => pickAvatar(emo)}
                    disabled={busy}
                    className={`flex h-9 w-9 items-center justify-center rounded-lg text-xl hover:bg-white/10 ${
                      avatar === emo ? 'bg-white/10 ring-1 ring-accent/50' : ''
                    }`}
                  >
                    {emo}
                  </button>
                ))}
              </div>
            ) : null}

            {/* Stats */}
            <div className="mt-4 grid grid-cols-4 gap-2">
              <StatTile label="Soirées" value={totals?.events ?? '—'} />
              <StatTile label="Suggestions" value={totals?.suggestions ?? '—'} />
              <StatTile label="Votes reçus" value={totals?.votesReceived ?? '—'} />
              <StatTile label="Badges" value={totals ? `${totals.badgeCount}/${ALL_BADGE_CODES.length}` : '—'} />
            </div>

            {/* Vitrine à trophées */}
            <div className="mt-5">
              <div className="mb-1 flex items-baseline justify-between">
                <h3 className="text-sm font-bold text-white">Vitrine à trophées</h3>
                <span className="text-[11px] text-white/40">
                  {overview ? `${totals.badgeCount} / ${ALL_BADGE_CODES.length}` : ''}
                </span>
              </div>
              <p className="mb-3 text-[11px] text-white/45">
                {self
                  ? 'Touche un badge gagné pour l’épingler (visible en live et sur ton profil).'
                  : 'Les badges gagnés par ce participant.'}
              </p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {ALL_BADGE_CODES.map((code) => {
                  const info = getBadgeInfo(code);
                  const earned = earnedByCode[code];
                  const isFeatured = featured === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleFeatured(code)}
                      disabled={!self || !earned || busy}
                      title={earned ? info.description : 'Badge à débloquer'}
                      className={`relative flex flex-col items-center gap-1 rounded-xl border px-1.5 py-2.5 text-center transition ${
                        earned
                          ? isFeatured
                            ? 'border-accent bg-accent/15'
                            : 'border-border bg-bg-soft'
                          : 'border-white/5 bg-white/[0.02] opacity-45'
                      } ${self && earned ? 'hover:bg-white/5' : ''}`}
                    >
                      {isFeatured ? (
                        <span className="absolute right-1 top-1 text-[9px]" aria-hidden>
                          📌
                        </span>
                      ) : null}
                      <span className={`text-2xl ${earned ? '' : 'grayscale'}`} aria-hidden>
                        {earned ? info.emoji : '🔒'}
                      </span>
                      <span className="line-clamp-2 text-[10px] font-medium leading-tight text-white/80">
                        {info.title}
                      </span>
                      {earned && earned.count > 1 ? (
                        <span className="text-[9px] text-white/40">×{earned.count}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-5 flex flex-col gap-2.5">
              {self && onManageAccount ? (
                <button type="button" className="btn-secondary w-full justify-center py-3 text-sm" onClick={onManageAccount}>
                  Compte &amp; connexion
                </button>
              ) : null}
              <button
                type="button"
                className="btn-ghost w-full justify-center py-2.5 text-xs text-white/55"
                onClick={() => onClose?.()}
              >
                {self ? 'Retour à la soirée' : 'Fermer'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
